import React, { useEffect, useState } from "react";
import api from "../api";
import { Link } from "react-router-dom";
import "./Home.css";
import currentArticles from "./articles/currentArticles";
import UpcomingEvent from "./UpcomingEvent";
import currentEvents from "./currentEvents";
import { getEventDate, getEventHours } from "./placeUtils";
import UpcomingInfo from "./UpcomingInfo";
import currentInfo from "./currentInfo";
import { useLanguage } from "./LanguageContext";
import { getLocalizedArticle } from "./localizedContent";
import ImagePlaceholder from "./ImagePlaceholder";

const numberOfItems = 3;
const categoryCards = [
  { title: "Kultura", description: "Muzea, teatry, kina i zabytki.", to: "/kultura" },
  { title: "Gastronomia", description: "Restauracje, kawiarnie i desery.", to: "/gastronomia" },
  { title: "Nauka", description: "Biblioteki, księgarnie i kursy.", to: "/nauka" },
  { title: "Eventy", description: "Sprawdź, co dzieje się w mieście.", to: "/eventy" },
];

function Home() {
  const { language, t } = useLanguage();
  const [places, setPlaces] = useState(null);
  const [events, setEvents] = useState(null);
  const [eventNumber, setEventNumber] = useState(0);
  const [celebrationsToShow, setCelebrationsToShow] = useState([]);
  const [loadingItems, setLoadingItems] = useState({ places: true, events: true });
  const [failedItems, setFailedItems] = useState({ places: false, events: false });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    fetchRandomItems("places");
    fetchRandomItems("events");
    setCelebrationsToShow(getUpcomingCelebrations());
  }, []);

  function fetchRandomItems(type) {
    const isPlaces = type === "places";
    const endpoint = isPlaces
      ? process.env.REACT_APP_RANDOM_PLACES_URL
      : process.env.REACT_APP_RANDOM_EVENTS_URL;

    api.get(endpoint + numberOfItems)
      .then((response) => {
        if (Array.isArray(response.data) && response.data.length > 0) {
          (isPlaces ? setPlaces : setEvents)(response.data);
        }
      })
      .catch((error) => {
        console.error(`Could not load ${type}.`, error);
        setFailedItems((current) => ({ ...current, [type]: true }));
      })
      .finally(() => {
        setLoadingItems((current) => ({ ...current, [type]: false }));
      });
  }

  function getUpcomingCelebrations() {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentDay = now.getDate();

    return currentEvents
      .map((event) => ({
        ...event,
        monthDistance: (event.month - currentMonth + 12) % 12,
      }))
      .filter((event) => event.monthDistance > 0 || event.day >= currentDay)
      .sort((firstEvent, secondEvent) => {
        return firstEvent.monthDistance - secondEvent.monthDistance || firstEvent.day - secondEvent.day;
      })
      .slice(0, numberOfItems);
  }

  function getCarouselLabel(item, type) {
    if (type === "places") return item.title;
    return `${item.title} - ${getEventDate(item)} godz: ${getEventHours(item)}`;
  }

  function getItemLink(item, type) {
    const suffix = type === "places" ? "/profil/" : "/profil_event/";
    return `/${item.category}/${item.subcategory}${suffix}${item._id}`;
  }

  function renderCarousel(items, type, id) {
    return (
      <div className={`home-carousel home-carousel-${type}`}>
        <div id={id} className="carousel slide carousel-fade" data-bs-ride="carousel">
          <div className="carousel-inner">
            {items.slice(0, numberOfItems).map((item, index) => (
              <div className={`carousel-item ${index === 0 ? "active" : ""}`} key={item._id} data-bs-interval="10000">
                <Link to={getItemLink(item, type)}>
                  <ImagePlaceholder
                    src={item?.images?.[0]?.img}
                    className="d-block w-100"
                    alt={item.title}
                    type={type === "events" ? "event" : "place"}
                  />
                  <div className="carousel-caption d-md-block">
                    <h4>{getCarouselLabel(item, type)}</h4>
                    {type === "events" && item.address && <p>{item.address.split(",")[0]}</p>}
                  </div>
                </Link>
              </div>
            ))}
          </div>
          <button className="carousel-control-prev" type="button" data-bs-target={`#${id}`} data-bs-slide="prev">
            <span className="carousel-control-prev-icon" aria-hidden="true" />
            <span className="visually-hidden">Poprzedni</span>
          </button>
          <button className="carousel-control-next" type="button" data-bs-target={`#${id}`} data-bs-slide="next">
            <span className="carousel-control-next-icon" aria-hidden="true" />
            <span className="visually-hidden">Następny</span>
          </button>
        </div>
      </div>
    );
  }

  function renderCarouselState(items, type, id, label) {
    if (items) return renderCarousel(items, type, id);
    if (loadingItems[type]) return <div className="loader" aria-label={`Ładowanie ${label}`} />;
    if (failedItems[type]) return <p className="carousel-error">Nie udało się wczytać {label}. Spróbuj ponownie później.</p>;
    return null;
  }

  const latestArticles = currentArticles.slice(-4).reverse();
  const latestInfo = currentInfo[currentInfo.length - 1];
  const hasCurrentInfo = latestInfo && new Date(latestInfo.date) >= new Date();
  const currentCelebration = celebrationsToShow[eventNumber];

  return (
    <main className="home-page">
      <section className="home-intro home-intro-compact">
        <h1>{t("homeHeadline")} <em>{t("homeHeadlineEmphasis")}</em></h1>
        <p className="intro-copy">{t("homeIntro")}</p>
      </section>

      <section className="home-section home-section-featured">
        <div className="section-heading"><p className="eyebrow">{t("nearDays")}</p><h2>{t("interestingEvents")}</h2></div>
        {renderCarouselState(events, "events", "events-carousel", "wydarzeń")}
      </section>

      <section className="home-section">
        <div className="section-heading"><p className="eyebrow">{t("weekend")}</p><h2>{t("interestingPlaces")}</h2></div>
        {renderCarouselState(places, "places", "places-carousel", "miejsc")}
      </section>

      {hasCurrentInfo && (
        <section className="home-section home-section-info">
          <div className="section-heading"><p className="eyebrow">{t("important")}</p><h2>{t("information")}</h2></div>
          <UpcomingInfo info={latestInfo} />
        </section>
      )}

      {currentCelebration && (
        <section className="home-section home-section-events">
          <div className="section-heading"><p className="eyebrow">{t("calendar")}</p><h2>{t("upcomingCelebrations")}</h2></div>
          <UpcomingEvent event={currentCelebration} />
          <div className="event-navigation" role="navigation" aria-label="Wybór uroczystości">
            {celebrationsToShow.map((event, index) => (
              <button type="button" className={eventNumber === index ? "active" : ""} onClick={() => setEventNumber(index)} key={event.title}>{index + 1}</button>
            ))}
          </div>
        </section>
      )}

      <section className="home-section articles-section">
        <div className="section-heading"><p className="eyebrow">{t("reading")}</p><h2>{t("latestArticles")}</h2></div>
        <div className="articles-grid">
          {latestArticles.map((article) => {
            const localizedArticle = getLocalizedArticle(article, language);

            return <article className="article-card" key={article.id}>
              <ImagePlaceholder
                src={localizedArticle.image}
                type="place"
                alt=""
              />
              <div className="article-card-content">
                <h3>{localizedArticle.title}</h3>
                <p>{localizedArticle.rawContent.substring(0, 120)}...</p>
                <Link to={`/artykuly/${localizedArticle.id}`} className="text-link">{t("readMore")} <span aria-hidden="true">&#8594;</span></Link>
              </div>
            </article>;
          })}
        </div>
        <Link to="/artykuly" className="all-articles-link">{t("allArticles")} <span aria-hidden="true">&#8594;</span></Link>
      </section>

      <section className="home-section category-section">
        <div className="section-heading"><p className="eyebrow">{t("browseYourWay")}</p><h2>{t("discoverByCategory")}</h2></div>
        <div className="category-grid">
          {categoryCards.map((category) => (
            <Link className="category-card" to={category.to} key={category.title}>
              <span className="category-card-title">{t({ Kultura: "culture", Gastronomia: "food", Nauka: "learning", Eventy: "events" }[category.title])}</span>
              <span className="category-card-description">{t({ Kultura: "cultureDescription", Gastronomia: "foodDescription", Nauka: "learningDescription", Eventy: "eventsDescription" }[category.title])}</span>
              <span className="category-card-arrow" aria-hidden="true">&#8594;</span>
            </Link>
          ))}
        </div>
      </section>

    </main>
  );
}

export default Home;
